import React, { useLayoutEffect, useEffect, useState, useContext } from 'react'
import { useNavigate } from 'react-router-dom'

import LoadingContext from '../../../context/Loading/Loading'
import EventDetailsContext from '../../../context/EventDetails/EventDetails'

import { useSheet } from '../../../providers/SideSheetProvider/SideSheetProvider'
import { useModal } from '../../../providers/ModalProvider/ModalProvider'

import { getEventAssignmentMap } from '../../../utilities/api'

import useSafeAsync from '../../../utilities/useSafeAsync'

import {
  fullHeightContainer,
  removeFullHeightContainer,
} from '../../../utilities/helpers'

import { PageLoadingContainer } from '../../PageLoadingContainer'
import { GroupTicketsMap } from './GroupTicketsMap'
import { Navigation } from './Navigation'
import { CurrentSelected } from '../../../providers/SideSheetProvider/SheetContent/CurrentSelected'
import { SelectGASeats } from '../../ModalContent'
import { PublishingDisclaimer } from '../PublishingDisclaimer'

export default function GroupTicketsSeatMapWrapper({
  id,
  eventId,
  ticketType,
}) {
  const safeSetState = useSafeAsync()
  const navigate = useNavigate()

  const { openModal } = useModal()
  const { openSheet, closeSheet } = useSheet()
  const { isLoading, showLoading, hideLoading } = useContext(LoadingContext)

  const { isEventPublishing, isEventPublished, hasEventEnded, isEventSoldout } =
    useContext(EventDetailsContext)

  const [event, setEvent] = useState()
  const [data, setData] = useState(null)
  const [background, setBackground] = useState(null)

  // Might not be so important for packagesMap but previously the ds for this was like:
  // { seats: {seatIds}, gaSeats: { sectionId: [seatIds]}}
  const [selectedSeatIds, setSelectedSeatIds] = useState({})
  const [soldMap, setSoldMap] = useState(new Map())
  const [reservedMap, setReservedMap] = useState(new Map())
  const [killMap, setKillMap] = useState(new Map())
  const [holdMap, setHoldMap] = useState(new Map())
  const [isMapDisabled, setIsMapDisabled] = useState(false)

  const localIsEventPublishing =
    isEventPublishing || event?.status === 'publishing'
  const localIsEventPublished =
    isEventPublished || ['published', 'on_sale'].includes(event?.status)
  const localHasEventEnded = hasEventEnded || event?.status === 'complete'
  const localIsEventSoldout = isEventSoldout || Boolean(event?.soldOut)

  useLayoutEffect(() => {
    // hide navbar
    const nav = document.getElementById('navbar')
    if (nav) nav.style.display = 'none'
    // hide event banner
    const banner = document.getElementById('banner')
    if (banner) banner.style.display = 'none'
    // hide sidebar
    const sidebarNav = document.getElementById('sidebarMenu')
    if (sidebarNav) sidebarNav.style.display = 'none'

    // modify container
    const container = document.getElementById('main-container')
    fullHeightContainer(container)

    // modify container
    const content = document.getElementById('main-content')
    content.classList.add('pb-0')
    content.classList.add('full-height-wrapper')
    content.classList.remove('spacer-md', 'spacer-md--with-banner')

    return () => {
      if (nav) nav.style.display = ''
      if (banner) banner.style.display = ''
      if (sidebarNav) sidebarNav.style.display = ''
      removeFullHeightContainer(container)
      content.classList.remove('pb-0')
      content.classList.remove('full-height-wrapper')
      content.classList.add('spacer-md', 'spacer-md--with-banner')
    }
  }, [])

  useEffect(() => {
    showLoading()
    loadEvent()
  }, [eventId])

  useEffect(() => {
    // Toggle sideSheet based on selected state
    if (selectedSeatIds && Object.keys(selectedSeatIds).length > 0) {
      openSheet(CurrentSelected, {
        selectedSeatIds: selectedSeatIds,
        handleRemove: clearSelectedSeats,
      })
    } else {
      closeSheet()
    }
  }, [selectedSeatIds])

  // controls if seatmap can be changed
  useEffect(() => {
    setIsMapDisabled(localHasEventEnded)
  }, [localHasEventEnded])

  const loadEvent = async () => {
    try {
      const res = await getEventAssignmentMap(eventId)

      safeSetState(() => {
        setEvent(res?.data)
        setData(res?.data?.seatmap?.mapping)
        setBackground(res?.data?.seatmap?.background)

        storeSoldTicketsInMap(res?.data)
        storeHoldsInMap(res?.data)
        storeKillsInMap(res?.data)
      })
    } catch (err) {
      console.error(err)
    } finally {
      hideLoading()
    }
  }

  if (
    localIsEventPublishing ||
    event?.status === 'publishing' ||
    event?.status === 'unpublished'
  ) {
    return (
      <PublishingDisclaimer disclaimer="Cannot assign tickets before event is fully published!" />
    )
  }

  const storeHoldsInMap = (event) => {
    const finalHoldsMap = new Map()

    if (!event?.inventory?.holds) {
      console.warn('No holds found in event inventory')
      setHoldMap(finalHoldsMap)
      return
    }

    // Iterate through each hold in the holds object
    Object.values(event.inventory.holds).forEach((hold) => {
      const hasSeats = Array.isArray(hold.seats) && hold.seats.length > 0
      const hasGaSeats =
        hold.gaSeats &&
        typeof hold.gaSeats === 'object' &&
        Object.keys(hold.gaSeats).length > 0
      const hasCategories =
        hold.categories &&
        typeof hold.categories === 'object' &&
        Object.keys(hold.categories).length > 0

      if (!hasSeats && !hasGaSeats && !hasCategories) {
        console.warn('Hold missing seats data:', hold)
        return
      }

      if (hasGaSeats) {
        Object.values(hold.gaSeats).forEach((seatIds) => {
          if (!Array.isArray(seatIds)) return

          seatIds.forEach((seatId) => {
            if (!seatId) return
            finalHoldsMap.set(seatId, {
              holdId: hold.id,
              holdName: hold.name,
              color: hold.color,
            })
          })
        })
      }

      if (hasSeats) {
        hold.seats.forEach((seatId) => {
          if (!seatId) {
            console.warn('Hold contains empty seatId:', hold)
            return
          }

          if (finalHoldsMap.has(seatId)) {
            console.warn(
              `SeatId ${seatId} already exists in holds map. Overwriting.`
            )
          }

          finalHoldsMap.set(seatId, {
            holdId: hold.id,
            holdName: hold.name,
            color: hold.color,
          })
        })
      }

      if (hasCategories) {
        Object.values(hold.categories).forEach((category) => {
          const categoryHasSeats =
            Array.isArray(category.seats) && category.seats.length > 0
          const categoryHasGaSeats =
            category.gaSeats &&
            typeof category.gaSeats === 'object' &&
            Object.keys(category.gaSeats).length > 0

          if (!categoryHasSeats && !categoryHasGaSeats) {
            console.warn('Category missing seats data:', category)
            return
          }

          if (categoryHasSeats) {
            category.seats.forEach((seatId) => {
              if (!seatId) {
                console.warn('Category contains empty seatId:', category)
                return
              }

              if (finalHoldsMap.has(seatId)) {
                console.warn(
                  `SeatId ${seatId} already exists in holds map. Overwriting with category.`
                )
              }

              finalHoldsMap.set(seatId, {
                holdId: hold.id,
                holdName: hold.name,
                categoryId: category.id,
                categoryName: category.name,
                color: category.color || hold.color,
              })
            })
          }

          if (categoryHasGaSeats) {
            Object.values(category.gaSeats).forEach((seatIds) => {
              if (!Array.isArray(seatIds)) return

              seatIds.forEach((seatId) => {
                if (!seatId) return
                finalHoldsMap.set(seatId, {
                  holdId: hold.id,
                  holdName: hold.name,
                  categoryId: category.id,
                  categoryName: category.name,
                  color: category.color || hold.color,
                })
              })
            })
          }
        })
      }
    })

    setHoldMap(finalHoldsMap)
  }

  const storeKillsInMap = (event) => {
    const finalKillsMap = new Map()

    if (!event?.inventory?.kills) {
      console.warn('No kills found in event inventory')
      setKillMap(finalKillsMap)
      return
    }

    // Iterate through each kill in the kills object
    Object.values(event.inventory.kills).forEach((kill) => {
      if (!kill.seats || !Array.isArray(kill.seats)) {
        console.warn('Kill missing seats array:', kill)
        return
      }

      // Add each seat from this kill to the map
      kill.seats.forEach((seatId) => {
        if (!seatId) {
          console.warn('Kill contains empty seatId:', kill)
          return
        }

        if (finalKillsMap.has(seatId)) {
          console.warn(
            `SeatId ${seatId} already exists in kills map. Overwriting.`
          )
        }

        // Map seatId to kill information
        finalKillsMap.set(seatId, {
          killId: kill.id,
          killName: kill.name,
          color: kill.color,
        })
      })

      // Handle categories (subkills) if they exist
      if (kill.categories && typeof kill.categories === 'object') {
        Object.values(kill.categories).forEach((category) => {
          if (!category.seats || !Array.isArray(category.seats)) {
            console.warn('Category missing seats array:', category)
            return
          }

          category.seats.forEach((seatId) => {
            if (!seatId) {
              console.warn('Category contains empty seatId:', category)
              return
            }

            if (finalKillsMap.has(seatId)) {
              console.warn(
                `SeatId ${seatId} already exists in kills map. Overwriting with category.`
              )
            }

            // Map seatId to category information (subkill)
            finalKillsMap.set(seatId, {
              killId: kill.id,
              killName: kill.name,
              categoryId: category.id,
              categoryName: category.name,
              color: category.color || kill.color,
            })
          })
        })
      }
    })

    setKillMap(finalKillsMap)
  }

  const storeSoldTicketsInMap = (event) => {
    const finalReservedMap = new Map()
    // Use reduce to build the single map
    const finalSoldMap = event.tickets.reduce((accMap, ticket) => {
      // Treat ANY non-available ticket as unavailable on the seatmap.
      // If we only block a subset of statuses, the UI can show seats as selectable
      // even though the backend won't reserve them (causing "reserved count mismatch").
      if (ticket?.on_sale_status && ticket.on_sale_status !== 'available') {
        const isReserved = ticket.on_sale_status === 'reserved'
        const isGeneralAdmission = ticket.generalAdmission
        if (isGeneralAdmission) {
          const sectionId = ticket.sectionId
          const seatId = ticket.seatId // Still need the seatId for the array

          if (!sectionId) {
            console.warn('Sold GA ticket missing sectionId:', ticket)
            return accMap // Skipping ticket if sectionId is missing
          }

          if (!seatId) {
            console.warn('Sold GA ticket missing seatId:', ticket)
            return accMap // Skipping ticket if seatId is missing
          }

          // Get the existing array for this section or initialize a new one
          // We access the accumulator map directly using the sectionId as the key
          const sectionSeats = accMap.get(sectionId) || []

          if (!Array.isArray(sectionSeats) && accMap.has(sectionId)) {
            console.error(
              `Collision or unexpected data type for key ${sectionId}. Expected array for GA section.`
            )
            return accMap // Skip this ticket to avoid corrupting map
          }

          sectionSeats.push(seatId)
          accMap.set(sectionId, sectionSeats) // Use sectionId as the key
        } else {
          const seatId = ticket.seatId
          if (!seatId) {
            console.warn('Sold reserved ticket missing seatId:', ticket)
            return accMap // Skipping ticket if seatId is missing
          }

          if (accMap.has(seatId) && Array.isArray(accMap.get(seatId))) {
            console.error(
              `Collision for key ${seatId}. It's already used as a GA section ID.`
            )
            return accMap // Skip this ticket to avoid corrupting map
          }

          if (isReserved) {
            finalReservedMap.set(
              seatId,
              event?.seatIdentityById?.[seatId] || true
            )
          } else {
            accMap.set(seatId, event?.seatIdentityById?.[seatId] || true)
          }
        }
      }
      // Return the accumulator map for the next ticket
      return accMap
    }, new Map()) // Start with a new empty Map

    setSoldMap(finalSoldMap)
    setReservedMap(finalReservedMap)
  }

  const handleGoBack = () => {
    navigate('..')
  }

  const clearSelectedSeats = () => {
    setSelectedSeatIds({})
  }

  const checkDisabled = () => {
    if (!selectedSeatIds?.seats && !selectedSeatIds?.gaSeats) return true
    return false
  }

  const openGAModal = (section) => {
    const totalSoldSeats = soldMap.get(section.sectionId)?.length || 0
    // Prefer non-held seats first so small quantities don't consume holds
    const availableSpots = section.spots.slice(totalSoldSeats)
    const seatsToAssignFrom = [
      ...availableSpots.filter((seatId) => !holdMap?.get(seatId)),
      ...availableSpots.filter((seatId) => holdMap?.get(seatId)),
    ]
    openModal(
      SelectGASeats,
      {
        setSelectedSeatIds: setSelectedSeatIds,
        section: section,
        seatsToAssignFrom: seatsToAssignFrom,
        holdMap,
      },
      { className: '', contentClassName: '' }
    )
  }

  return (
    <>
      {isLoading ? (
        <PageLoadingContainer style="without-sidebar" />
      ) : (
        <>
          <Navigation
            ticketType={ticketType}
            event={event}
            configuration={event?.seatmap}
            isMapDisabled={isMapDisabled}
            isEventPublished={localIsEventPublished}
            hasEventEnded={localHasEventEnded}
            selectedSeatIds={selectedSeatIds}
            isExistingOrder={Boolean(id)}
            handleGoBack={handleGoBack}
            isDisabled={checkDisabled()}
            data={data}
          />
          {data && background && (
            <GroupTicketsMap
              isEventPublishing={localIsEventPublishing}
              data={data}
              background={background}
              selectedSeatIds={selectedSeatIds}
              setSelectedSeatIds={setSelectedSeatIds}
              isMapDisabled={isMapDisabled}
              soldMap={soldMap}
              reservedMap={reservedMap}
              killMap={killMap}
              holdMap={holdMap}
              openGAModal={openGAModal}
              event={event}
            />
          )}
        </>
      )}
    </>
  )
}
